import {
  Badge,
  Box,
  Flex,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Spinner,
  Text,
  useDisclosure,
} from '@elkaso-app/web-design';
import type { DealHistoryEntryDto } from 'apis/exclusive-deals';
import { useGetDealHistoryApi } from 'apis/exclusive-deals';
import { formatPrice } from 'pages/prices/utils/matrix-display';
import type { VariantUom } from 'pages/prices/utils/uom-display';
import { dealUnitWord } from 'pages/prices/utils/uom-display';
import { FiClock } from 'react-icons/fi';
import { Link } from 'react-router-dom';

export interface PriceHistoryPopoverProps {
  dealId: number;
  // The variant's ladder, to resolve each snapshot's recorded rung (a stored
  // POSITION, e.g. PACK) to its unit word. Absent = prices render unit-less,
  // exactly as before.
  variant?: VariantUom;
}

const sourceLabels: Record<string, { label: string; colorScheme: string }> = {
  cms_update: { label: 'CMS', colorScheme: 'blue' },
  sheet_upload: { label: 'Sheet', colorScheme: 'purple' },
  cms_bulk_upsert: { label: 'Bulk', colorScheme: 'purple' },
  price_list_import: { label: 'Import', colorScheme: 'orange' },
  // Inquiry desk: a quote saved with "Save to Price tool", or an award —
  // the negotiated figure became the supplier's standing price.
  inquiry_quote: { label: 'Quote', colorScheme: 'teal' },
  inquiry_award: { label: 'Awarded', colorScheme: 'green' },
};

const formatWhen = (iso: string): string =>
  new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const HistoryRow = ({
  entry,
  olderEntry,
  variant,
}: {
  entry: DealHistoryEntryDto;
  olderEntry?: DealHistoryEntryDto;
  variant?: VariantUom;
}) => {
  const source = sourceLabels[entry.source] ?? { label: entry.source, colorScheme: 'gray' };
  const previousPrice = olderEntry?.snapshot.dealPrice;
  const priceChanged = previousPrice != null && previousPrice !== entry.snapshot.dealPrice;
  // Only a RECORDED rung renders — inferring one for pre-dealUnit snapshots
  // would present a guess as history. A same-price save that only moved the
  // rung still reads as a change via the "from" suffix below.
  const unitWord = (snapshot: DealHistoryEntryDto['snapshot']): string | null =>
    variant && snapshot.dealUnit ? dealUnitWord(variant, snapshot.dealUnit) : null;
  const unit = unitWord(entry.snapshot);
  const previousUnit = olderEntry ? unitWord(olderEntry.snapshot) : null;
  const unitChanged = previousUnit != null && previousUnit !== unit;

  return (
    <Box py='7px' _notLast={{ borderBottom: '1px solid', borderColor: 'gray.100' }}>
      <Flex align='baseline' justify='space-between' gap='10px'>
        <Text fontSize='13px' fontWeight='bold' whiteSpace='nowrap' sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatPrice(entry.snapshot.dealPrice)}
          <Text as='span' fontSize='9.5px' fontWeight='semibold' color='gray.400' ms='3px'>
            {unit ? `AED/${unit}` : 'AED'}
          </Text>
          {(priceChanged || unitChanged) && (
            <Text as='span' fontSize='10.5px' fontWeight='medium' color='gray.400' ms='6px'>
              from {formatPrice(previousPrice ?? entry.snapshot.dealPrice)}
              {unitChanged ? `/${previousUnit}` : ''}
            </Text>
          )}
        </Text>
        <Text fontSize='10.5px' color='gray.500' whiteSpace='nowrap' sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatWhen(entry.createdAt)}
        </Text>
      </Flex>
      <Flex align='center' gap='6px' mt='1px'>
        <Text fontSize='11px' color='gray.600' noOfLines={1}>
          {entry.author ? entry.author.name || `User #${entry.author.id}` : 'System'}
        </Text>
        <Badge colorScheme={source.colorScheme} variant='subtle' fontSize='8.5px'>
          {source.label}
        </Badge>
        {entry.snapshot.inquiry && (
          <Text
            as={Link}
            to={`/app/inquiry-processor/inquiry/${entry.snapshot.inquiry.inquiryId}`}
            fontSize='10.5px'
            fontWeight='semibold'
            color='red.600'
            whiteSpace='nowrap'
            ms='auto'
            _hover={{ textDecoration: 'underline' }}>
            Inquiry #{entry.snapshot.inquiry.inquiryId}
          </Text>
        )}
      </Flex>
    </Box>
  );
};

// Per-cell change log: who set which price when (supplier_price_change_logs
// via the deal-history endpoint). The clock trigger sits inside an editable
// cell, so it must stop click propagation — otherwise opening the history
// would also flip the cell into edit mode. Fetches only while open, newest
// first; shows the latest page of 10 with the total count in the header.
export const PriceHistoryPopover = ({ dealId, variant }: PriceHistoryPopoverProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data, isLoading } = useGetDealHistoryApi(dealId, 1, 10, { enabled: isOpen });
  const entries = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement='bottom-start' isLazy>
      <PopoverTrigger>
        <IconButton
          aria-label='Price history'
          title='Price history'
          icon={<FiClock size={11} />}
          size='xs'
          variant='ghost'
          color='gray.400'
          minW='20px'
          h='20px'
          borderRadius='6px'
          _hover={{ color: 'gray.600', bg: 'blackAlpha.100' }}
          onClick={(e) => e.stopPropagation()}
        />
      </PopoverTrigger>
      <Portal>
        {/* Portaled + popover z-index so it clears the sticky header/column. */}
        <PopoverContent w='300px' zIndex='popover' onClick={(e) => e.stopPropagation()}>
          <PopoverArrow />
          <PopoverHeader border='none' pb='2px'>
            <Text
              fontSize='11px'
              fontWeight='semibold'
              letterSpacing='0.06em'
              textTransform='uppercase'
              color='gray.500'>
              Price history{total ? ` · ${total} change${total === 1 ? '' : 's'}` : ''}
            </Text>
          </PopoverHeader>
          <PopoverBody pt='2px' maxH='320px' overflowY='auto'>
            {isLoading ? (
              <Flex justify='center' py='16px'>
                <Spinner size='sm' color='red.500' />
              </Flex>
            ) : entries.length ? (
              <>
                {entries.map((entry, index) => (
                  <HistoryRow key={entry.id} entry={entry} olderEntry={entries[index + 1]} variant={variant} />
                ))}
                {total > entries.length && (
                  <Text fontSize='10.5px' color='gray.400' pt='6px'>
                    Showing the latest {entries.length} of {total}
                  </Text>
                )}
              </>
            ) : (
              <Text fontSize='12px' color='gray.500' py='8px'>
                No changes recorded for this price yet.
              </Text>
            )}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
